import type { TFunction } from "i18next";
import type { U3130ActivePayrollPeriodItem, U3130SommairePaiePayrollPeriodOption } from "../U3130TimesheetModel";

/** Date calendaire locale YYYY-MM-DD (pour chevauchement avec les dates ISO du backend). */
function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoToYmd(iso: string): string {
  const s = iso?.trim() ?? "";
  return s.length >= 10 ? s.slice(0, 10) : "";
}

/** Identifiant stable combo (évite l’ambiguïté période 1 / plusieurs exercices). */
export function u3130ActivePayrollPeriodOptionId(yearCode: string, periodNumber: number): string {
  return `${String(yearCode).trim()}|${periodNumber}`;
}

function sortActivePayrollPeriodsChronologically(a: U3130ActivePayrollPeriodItem, b: U3130ActivePayrollPeriodItem): number {
  const ya = String(a.yearCode ?? "").trim();
  const yb = String(b.yearCode ?? "").trim();
  if (ya && yb) {
    const cy = ya.localeCompare(yb, undefined, { numeric: true });
    if (cy !== 0) return cy;
  }
  const sa = isoToYmd(a.startAt);
  const sb = isoToYmd(b.startAt);
  if (sa.length >= 10 && sb.length >= 10 && sa !== sb) {
    return sa.localeCompare(sb);
  }
  return a.periodNumber - b.periodNumber;
}

/**
 * Transforme `activePayrollPeriod` (initLU) en options pour le combo du rapport sommaire paie.
 */
export function u3130MapActivePayrollPeriodsToReportOptions(
  items: U3130ActivePayrollPeriodItem[],
  t: TFunction,
  ns: string,
): U3130SommairePaiePayrollPeriodOption[] {
  return [...items]
    .filter((row) => row != null && Number.isFinite(Number(row.periodNumber)))
    .sort(sortActivePayrollPeriodsChronologically)
    .map((row) => {
      const yc = String(row.yearCode ?? "").trim();
      const ymdStart = isoToYmd(row.startAt);
      let labelYear: string | number = yc;
      if (!labelYear) {
        let y = ymdStart.length >= 4 ? Number(ymdStart.slice(0, 4)) : NaN;
        if (!Number.isFinite(y)) {
          const d = new Date(row.startAt);
          y = Number.isNaN(d.getTime()) ? new Date().getFullYear() : d.getUTCFullYear();
        }
        labelYear = y;
      }
      const id = yc ? u3130ActivePayrollPeriodOptionId(yc, row.periodNumber) : String(row.periodNumber);
      return {
        id,
        label: t("lblPayrollPeriodComboItem", { ns, year: labelYear, periodNumber: row.periodNumber }),
        startAt: row.startAt,
        endAt: row.endAt,
        yearCode: yc || undefined,
        periodNumber: row.periodNumber,
      };
    });
}

/**
 * Période par défaut pour le rapport : celle **précédant** la période calendaire « courante »
 * (celle qui contient aujourd’hui), car la paie porte en général sur la période déjà close, pas sur l’en cours.
 * Si aujourd’hui est dans la 1re période de la liste, on garde cette entrée (pas d’antérieure dans le tableau).
 * Si aucune période ne contient aujourd’hui, repli sur la dernière ou la première selon la position de la date.
 */
export function u3130PickDefaultPayrollPeriodForReport(options: U3130SommairePaiePayrollPeriodOption[]): U3130SommairePaiePayrollPeriodOption | null {
  if (!options.length) return null;
  const today = localYmd(new Date());
  const containingIdx = options.findIndex((o) => {
    const s = isoToYmd(o.startAt);
    const e = isoToYmd(o.endAt);
    if (s.length < 10 || e.length < 10) return false;
    return today >= s && today <= e;
  });
  if (containingIdx >= 1) {
    return options[containingIdx - 1];
  }
  if (containingIdx === 0) {
    return options[0];
  }
  const firstStart = isoToYmd(options[0].startAt);
  if (firstStart.length >= 10 && today < firstStart) {
    return options[0];
  }
  const last = options[options.length - 1];
  const lastEnd = isoToYmd(last.endAt);
  if (lastEnd.length >= 10 && today > lastEnd && options.length >= 2) {
    return options[options.length - 2];
  }
  if (lastEnd.length >= 10 && today > lastEnd) {
    return last;
  }
  return options[0];
}
